import type { ThemeOptions, Theme } from '@mui/material';
import { createTheme } from '@mui/material/styles';

const customThemeOptions: ThemeOptions = {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          html: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&:focus, &:focus-within': {
            outline: `2px solid #316BBE`,
          },
        },
        containedSecondary: {
          backgroundColor: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#FFFFFF',
            color: '#002677',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus, &:focus-within': {
            outline: `2px solid #316BBE`,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          display: 'inline-block',
          '&:focus, &:focus-within, &:focus-visible': {
            outline: `2px auto #316BBE`,
            outlineOffset: '3px',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.ccg-Mui-checked': {
            color: '#196DCD',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: 0,
        },
      },
    },
  },
};

export const OptumDefault: Theme = createTheme(
  {
    breakpoints: {
      keys: ['xs', 'sm', 'md', 'lg', 'xl'],
      values: { xs: 0, sm: 541, md: 769, lg: 1049, xl: 1441 },
      unit: 'px',
    },
    direction: 'ltr',
    components: {
      MuiInputLabel: {
        defaultProps: { shrink: false },
        styleOverrides: {
          outlined: {
            pointerEvents: 'initial',
            transform: 'none',
          },
          formControl: { position: 'relative' },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            maxWidth: 'unset !important',
            color: '#323334',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '20px',
            marginBottom: '4px',
            '@media (max-width:540.95px)': {
              fontSize: '14.22px',
              fontWeight: '700',
              lineHeight: '18px',
            },
            '& .labelInstructionText': {
              color: '#4B4D4F',
              fontFamily: '"Optum Sans", Arial, sans-serif',
              lineHeight: '18px',
              fontSize: '14.22px',
              fontWeight: '400',
              '@media (max-width:540.95px)': {
                fontSize: '14.22px',
                fontWeight: '700',
                lineHeight: '18px',
              },
            },
            '& .formTooltipIcon': {
              color: '#0c55B8',
              height: '18px',
              width: '18px',
              marginLeft: 4,
            },
            '& .tooltipLabel': {
              color: '#0c55B8',
              marginLeft: 4,
            },
            '&.Mui-focused': { color: '#323334' },
            '&.Mui-disabled': { color: '#B1B2B4' },
            '&.Mui-error': { color: '#C40000' },
          },
          asterisk: { color: '#C40000' },
        },
      },
      MuiCheckbox: {
        defaultProps: { disableRipple: true, color: 'primary' },
        styleOverrides: {
          root: {
            color: '#4B4D4F',
            padding: 0,
            marginRight: 8,
            maxWidth: 24,
            maxHeight: 24,
            minWidth: 24,
            '&:hover': { backgroundColor: 'transparent' },
            '&:focus-within': { outline: 'none' },
            '&.Mui-focusVisible:after, &:focus-within:after, &:active:after, &:focus-within:after':
              { content: 'none' },
            '&:active': { color: '#19191A' },
            '&.MuiCheckboxChip-root': {
              '&': {
                height: '1px',
                opacity: 0,
                position: 'absolute',
                width: '1px',
                '& + .MuiFormControlLabel-label': {
                  background: '#FFF',
                  border: '1px solid #0C55B8',
                  borderRadius: '4px',
                  color: '#0C55B8',
                  fontFamily: '"Optum Sans", Arial, sans-serif',
                  fontSize: '14.22px',
                  fontWeight: 700,
                  padding: '4px 10px',
                  '&:hover': {
                    backgroundColor: '#0C55B8',
                    borderColor: '#0C55B8',
                    color: '#FFF',
                  },
                },
              },
              '&.Mui-checked': {
                borderRadius: 0,
                left: '5px',
                opacity: 1,
                '& .MuiSvgIcon-root': {
                  color: '#FFF',
                  width: '20px',
                },
                '& + .MuiFormControlLabel-label': {
                  background: '#002677',
                  borderColor: '#002677',
                  color: '#FFF',
                  paddingLeft: '30px',
                  '&:focus': { padding: '5px' },
                  '&:hover': {
                    backgroundColor: '#0C55B8',
                    borderColor: '#0C55B8',
                    color: '#FFF',
                  },
                },
              },
              '&.MuiCheckboxChip-small': {
                '& + .MuiFormControlLabel-label': {
                  fontSize: '12.64px',
                  padding: '3px 8px',
                },
                '&.Mui-checked': {
                  '& .MuiSvgIcon-root': {
                    color: '#FFF',
                    width: '15px',
                  },
                  '& + .MuiFormControlLabel-label': {
                    paddingLeft: '28px',
                  },
                },
              },
            },
            '&.MuiCheckbox-indeterminate': { color: '#0c55B8' },
          },
          colorPrimary: {
            '&.Mui-checked': {
              color: '#0c55B8',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#002677',
              },
            },
            '&.Mui-disabled': { color: '#B1B2B4' },
          },
          colorSecondary: {
            '&.Mui-checked': {
              color: '#0c55B8',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#002677',
              },
            },
            '&.Mui-disabled': { color: '#B1B2B4' },
          },
          indeterminate: { color: '#0c55B8' },
        },
      },
      MuiFormGroup: {
        styleOverrides: {
          root: { marginBottom: 8 },
          row: {
            '> .MuiFormControlLabel-root': {
              marginRight: 16,
              paddingRight: 4,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: '#4B4D4F',
            fontFamily: '"Optum Sans", Arial, sans-serif',
            fontSize: '14.22px',
            fontWeight: '400',
            lineHeight: '18px',
            marginLeft: 0,
            marginRight: 0,
            marginTop: 0,
            marginBottom: 4,
            '&.labelInstructionText': {
              color: '#4B4D4F',
              fontFamily: '"Optum Sans", Arial, sans-serif',
              lineHeight: '18px',
              fontSize: '14.22px',
              fontWeight: '400',
            },
            '&.switclabelInstructionText': {
              gap: '24px',
              marginLeft: '5px',
              color: '#4B4D4F',
              fontFamily: '"Optum Sans", Arial, sans-serif',
              lineHeight: '20px',
              fontSize: '14px',
              fontWeight: '400',
              '@media (max-width:540.95px)': {
                lineHeight: '20px',
                fontSize: '14px',
                fontWeight: '400',
              },
            },
            '& .formErrorMessage': {
              color: '#C40000',
              fontFamily: '"Optum Sans", Arial, sans-serif',
              lineHeight: '18px',
              fontSize: '14.22px',
              fontWeight: '400',
            },
          },
          contained: {
            marginLeft: 0,
            marginRight: 0,
            marginTop: 0,
            marginBottom: 4,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorError: {
            color: '#C40000',
            height: '18px',
            width: '18px',
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
      MuiInputAdornment: {
        styleOverrides: {
          positionEnd: {
            paddingTop: '0px',
            paddingRight: 0,
            paddingBottom: '0px',
            paddingLeft: '0px',
            '& > button.MuiButton-contained': {
              height: '100%',
              minHeight: 38,
              minWidth: '40px',
              backgroundColor: '#002677',
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
              borderTopLeftRadius: '0',
              borderBottomLeftRadius: '0',
              paddingTop: '8px',
              paddingRight: '8px',
              paddingBottom: '8px',
              paddingLeft: '8px',
              borderColor: '#002677',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#FBF9F4',
              '& .MuiSvgIcon-root': {
                height: '24px',
                width: '24px',
              },
              '&:hover': {
                backgroundColor: '#0c55B8',
                color: '#FBF9F4',
                borderColor: '#0c55B8',
              },
              '&:active': {
                backgroundColor: '#FBF9F4',
                color: '#0c55B8',
                borderColor: '#0c55B8',
              },
            },
            '& .MuiButtonBase-root.inputClearButton': {
              marginTop: '8px',
              marginRight: '8px',
              marginBottom: '8px',
              marginLeft: '8px',
              padding: 0,
              '&:focus, &.Mui-focusVisible': {
                border: 'none',
                borderRadius: 0,
                outline: '2px solid #0c55B8',
              },
              '&.Mui-focusVisible:after': { content: 'none' },
            },
            '& > .MuiSvgIcon-root': { marginRight: 14 },
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: { notched: false },
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            borderTopRightRadius: '4px',
            borderBottomRightRadius: '4px',
            borderBottomLeftRadius: '4px',
            borderTopLeftRadius: '4px',
            marginBottom: '4px',
            '&:hover .ccg-MuiOutlinedInput-notchedOutline': {
              borderColor: '#19191A',
              borderStyle: 'solid',
              borderWidth: '2px',
            },
            '&.Mui-focused .ccg-MuiOutlinedInput-notchedOutline':
              {
                borderColor: '#0c55B8',
                borderStyle: 'solid',
                borderWidth: '3px',
              },
            '&.Mui-error .ccg-MuiOutlinedInput-notchedOutline': {
              borderColor: '#C40000',
              borderStyle: 'solid',
              borderWidth: '2px',
            },
            '&.Mui-disabled .ccg-MuiOutlinedInput-notchedOutline':
              {
                borderColor: '#4B4D4F',
                borderStyle: 'solid',
                borderWidth: '1px',
              },
            '&.Mui-error.Mui-focused .ccg-MuiOutlinedInput-notchedOutline':
              { borderColor: '#0c55B8' },
          },
          input: {
            height: 'unset',
            backgroundColor: '#FFFFFF',
            color: '#4B4D4F',
            fontFamily: '"Optum Sans", Arial, sans-serif',
            fontSize: '16px',
            lineHeight: '20px',
            paddingTop: '10px',
            paddingRight: '12px',
            paddingBottom: '10px',
            paddingLeft: '12px',
          },
          notchedOutline: {
            borderColor: '#6E7072',
            borderStyle: 'solid',
            borderWidth: '1px',
          },
          adornedEnd: { paddingRight: 0 },
        },
      },
      MuiSelect: {
        defaultProps: { native: true },
        styleOverrides: { icon: { fill: '#0C55B8' } },
      },
      MuiNativeSelect: {
        styleOverrides: { icon: { fill: '#0C55B8' } },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            padding: '12px 4px 12px 0',
            margin: 0,
            '&:active, &:focus, &:focus-within': {
              outline: '2px solid #0c55B8',
            },
            '&:focus-within:after': { content: 'none' },
            '&.checkboxListHeader > .MuiFormControlLabel-label':
              {
                fontWeight: 'bold',
              },
            '&.checkboxListItem': { marginLeft: 16 },
            '&.formControlLabelSwitch': {
              margin: 0,
              padding: '4px 0',
              '.MuiFormControlLabel-label': {
                color: '#4B4D4F',
                lineHeight: '24px',
                fontSize: '18px',
                fontWeight: '700',
                '@media (max-width:540.95px)': {
                  lineHeight: '24px',
                  fontSize: '18px',
                  fontWeight: '700',
                },
              },
            },
            '&.MuiFormControlLabel-labelPlacementEnd label': {
              marginLeft: 8,
            },
            '&.MuiFormControlLabelRadioChip-root, &.MuiFormControlLabelCheckboxChip-root':
              {
                marginBottom: '8px',
                padding: 0,
                position: 'relative',
                '&:focus-within, &:active': {
                  outlineOffset: '3px',
                },
              },
          },
          labelPlacementStart: {
            '&.formControlLabelSwitch': {
              display: 'flex',
              justifyContent: 'space-between',
              '.MuiFormControlLabel-label': { marginRight: 24 },
            },
          },
        },
      },
      MuiRadio: {
        defaultProps: { disableRipple: true, color: 'primary' },
        styleOverrides: {
          root: {
            color: '#4B4D4F',
            marginRight: 8,
            maxWidth: 24,
            maxHeight: 24,
            minWidth: 24,
            '&:hover': { backgroundColor: 'transparent' },
            '&:focus-within': { outline: 'none' },
            '&.Mui-focusVisible:after, &:focus-within:after': {
              content: 'none',
            },
            '&:active': { color: '#19191A' },
            '&.Mui-disabled': { color: '#B1B2B4' },
            '&.MuiRadioChip-root': {
              '&': {
                height: '1px',
                opacity: 0,
                position: 'absolute',
                width: '1px',
                '& + .MuiFormControlLabel-label': {
                  background: '#FFF',
                  border: '1px solid #0C55B8',
                  borderRadius: '4px',
                  color: '#0C55B8',
                  fontFamily: '"Optum Sans", Arial, sans-serif',
                  fontSize: '14.22px',
                  fontWeight: 700,
                  padding: '4px 10px',
                  '&:hover': {
                    backgroundColor: '#0C55B8',
                    borderColor: '#0C55B8',
                    color: '#FFF',
                  },
                },
              },
              '&.Mui-checked': {
                borderRadius: 0,
                left: '5px',
                opacity: 1,
                '& .MuiSvgIcon-root': {
                  color: '#FFF',
                  width: '20px',
                },
                '& + .MuiFormControlLabel-label': {
                  background: '#002677',
                  borderColor: '#002677',
                  color: '#FFF',
                  paddingLeft: '30px',
                  '&:focus': { padding: '5px' },
                  '&:hover': {
                    backgroundColor: '#0C55B8',
                    borderColor: '#0C55B8',
                    color: '#FFF',
                  },
                },
              },
              '&.MuiRadioChip-small': {
                '& + .MuiFormControlLabel-label': {
                  fontSize: '12.64px',
                  padding: '3px 8px',
                },
                '&.Mui-checked': {
                  '& .MuiSvgIcon-root': {
                    color: '#FFF',
                    width: '15px',
                  },
                  '& + .MuiFormControlLabel-label': {
                    paddingLeft: '28px',
                  },
                },
              },
            },
          },
          colorPrimary: {
            '&.Mui-checked': {
              color: '#0c55B8',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#002677',
              },
            },
            '&.Mui-disabled': { color: '#B1B2B4' },
          },
          colorSecondary: {
            '&.Mui-checked': {
              color: '#0c55B8',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#002677',
              },
            },
            '&.Mui-disabled': { color: '#B1B2B4' },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': { boxShadow: 'none' },
            '&.Mui-focusVisible:after': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: '-4px',
              bottom: '-4px',
              left: '-4px',
              right: '-4px',
              borderRadius: 0,
              outline: '2px solid #0c55B8',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          color: 'primary',
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            borderBottomLeftRadius: '46px',
            borderBottomRightRadius: '46px',
            borderTopLeftRadius: '46px',
            borderTopRightRadius: '46px',
            padding: '10px 24px 10px 24px',
            textTransform: 'none',
            '&.Mui-focusVisible': {
              boxShadow: 'none',
              position: 'relative',
            },
            '&.Mui-disabled': {
              backgroundColor: '#F2F2F2',
              borderColor: '#F2F2F2',
              color: '#4B4D4F',
            },
            '@media (max-width:768.95px)': {
              padding: '10px 24px 10px 24px',
            },
          },
          sizeSmall: {
            fontSize: '14.22px',
            lineHeight: '18px',
            fontWeight: '700',
            maxHeight: '32px',
            padding: '7px 16px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&.Mui-focusVisible': { boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
            '&.Mui-disabled': {
              boxShadow: 'none',
              backgroundColor: '#F2F2F2',
              borderColor: '#F2F2F2',
              color: '#4B4D4F',
            },
          },
          containedPrimary: {
            backgroundColor: '#002677',
            borderRadius: '46px',
            borderColor: '#002677',
            borderStyle: 'solid',
            borderWidth: '1px',
            color: '#FBF9F4',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '20px',
            maxHeight: '40px',
            '&:hover': {
              backgroundColor: '#0c55B8',
              borderColor: '#0c55B8',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#FBF9F4',
            },
            '&:active': {
              backgroundColor: '#FBF9F4',
              borderColor: '#0c55B8',
              boxShadow: 'none',
              color: '#0c55B8',
            },
            '@media (max-width:768.95px)': {
              fontSize: '16px',
              lineHeight: '20px',
              fontWeight: '700',
              maxHeight: '44px',
              padding: '10px 24px 10px 24px',
            },
            '&.highContrastPrimary': {
              backgroundColor: '#FBF9F4',
              borderRadius: '46px',
              borderColor: '#FBF9F4',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#002677',
              fontSize: '16px',
              fontWeight: '700',
              lineHeight: '20px',
              maxHeight: '40px',
              padding: '10px 24px',
              '&:hover': {
                backgroundColor: '#FBF9F4',
                borderColor: '#FBF9F4',
                color: '#0c55B8',
              },
              '&:active': {
                backgroundColor: 'transparent',
                borderColor: '#FBF9F4',
                color: '#FBF9F4',
              },
              '&.Mui-focusVisible:after': {
                outlineColor: '#FFF',
              },
              '@media (max-width:768.95px)': {
                lineHeight: '20px',
                fontSize: '16px',
                fontWeight: '700',
                maxHeight: '44px',
                paddingTop: '10px',
                paddingRight: '24px',
                paddingBottom: '10px',
                paddingLeft: '24px',
              },
            },
          },
          containedSecondary: {
            backgroundColor: '#FBF9F4',
            borderRadius: '46px',
            borderColor: '#002677',
            borderStyle: 'solid',
            borderWidth: '1px',
            color: '#002677',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '20px',
            maxHeight: '40px',
            padding: '10px 24px',
            '&:hover': {
              backgroundColor: '#FBF9F4',
              borderColor: '#0c55B8',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#0c55B8',
            },
            '&:active': {
              backgroundColor: '#0c55B8',
              borderColor: '#0c55B8',
              color: '#FBF9F4',
            },
            '@media (max-width:768.95px)': {
              fontSize: '16px',
              lineHeight: '20px',
              fontWeight: '700',
              maxHeight: '44px',
              padding: '10px 24px 10px 24px',
            },
          },
          outlinedInfo: {
            backgroundColor: '#FFFFFF',
            borderRadius: '46px',
            borderColor: '#323334',
            borderStyle: 'solid',
            borderWidth: '1px',
            color: '#323334',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '20px',
            maxHeight: '40px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#F2F2F2',
              borderColor: '#323334',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#323334',
            },
            '&:active': {
              backgroundColor: '#323334',
              borderColor: '#323334',
              color: '#FFFFFF',
            },
            '@media (max-width:768.95px)': {
              fontSize: '16px',
              lineHeight: '20px',
              fontWeight: '700',
              maxHeight: '44px',
              padding: '10px 24px 10px 24px',
            },
          },
          outlinedPrimary: {
            backgroundColor: '#FBF9F4',
            borderRadius: '46px',
            borderColor: '#002677',
            borderStyle: 'solid',
            borderWidth: '1px',
            color: '#002677',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '20px',
            maxHeight: '40px',
            padding: '10px 24px',
            '&:hover': {
              backgroundColor: '#FBF9F4',
              borderColor: '#0c55B8',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#0c55B8',
            },
            '&:active': {
              backgroundColor: '#0c55B8',
              borderColor: '#0c55B8',
              color: '#FBF9F4',
            },
          },
          outlinedSecondary: {
            '&.highContrastSecondary': {
              backgroundColor: 'transparent',
              borderRadius: '46px',
              borderColor: '#FBF9F4',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#FBF9F4',
              fontSize: '16px',
              fontWeight: '700',
              lineHeight: '20px',
              maxHeight: '40px',
              padding: '10px 24px',
              '&:hover': {
                backgroundColor: '#FBF9F4',
                borderColor: '#FBF9F4',
                borderStyle: 'solid',
                borderWidth: '1px',
                color: '#0c55B8',
              },
              '&:active': {
                backgroundColor: '#0c55B8',
                borderColor: '#FBF9F4',
                color: '#FBF9F4',
              },
              '&.Mui-focusVisible:after': {
                outlineColor: '#FFF',
              },
              '@media (max-width:768.95px)': {
                lineHeight: '20px',
                fontSize: '16px',
                fontWeight: '700',
                maxHeight: '44px',
                paddingTop: '10px',
                paddingRight: '24px',
                paddingBottom: '10px',
                paddingLeft: '24px',
              },
            },
          },
          containedSizeSmall: {
            fontSize: '14.22px',
            lineHeight: '18px',
            fontWeight: '700',
            maxHeight: '32px',
            padding: '7px 16px',
            '@media (max-width:768.95px)': {
              fontSize: '14.22px',
              lineHeight: '18px',
              fontWeight: '700',
              maxHeight: '32px',
              padding: '7px 16px',
            },
          },
          outlinedSizeSmall: {
            fontSize: '14.22px',
            lineHeight: '18px',
            fontWeight: '700',
            maxHeight: '32px',
            padding: '7px 16px',
            '@media (max-width:768.95px)': {
              fontSize: '14.22px',
              lineHeight: '18px',
              fontWeight: '700',
              maxHeight: '32px',
              padding: '7px 16px',
            },
          },
          iconSizeSmall: {
            '& > *:first-child': { fontSize: 20 },
          },
          iconSizeMedium: {
            '& > *:first-child': { fontSize: 24 },
          },
          iconSizeLarge: {
            '& > *:first-child': { fontSize: 24 },
          },
          text: {
            '@media (min-width:0px)': {
              padding: '2px',
              lineHeight: '20px',
              fontSize: '16px',
              fontWeight: '700',
              '&.MuiButton-fullWidth': { width: 'auto' },
              '& .MuiButton-endIcon ': { marginLeft: 2 },
              '& .MuiButton-startIcon ': { marginRight: 2 },
            },
          },
          textPrimary: {
            color: '#0c55B8',
            '&:hover': {
              backgroundColor: 'rgba(25,25,26,0.04)',
              borderRadius: '4px',
              color: '#002677',
            },
            '& .MuiTouchRipple-root': { display: 'none' },
          },
          textSecondary: {
            color: '#0c55B8',
            '&:hover': {
              backgroundColor: 'rgba(25,25,26,0.04)',
              color: '#002677',
            },
          },
          textSizeSmall: {
            padding: '2px',
            fontSize: '14.22px',
            '@media (max-width:768.95px)': {
              padding: '2px',
              fontSize: '14.22px',
            },
          },
          textSizeLarge: { padding: '2px', fontSize: '16px' },
        },
      },
      MuiIconButton: {
        defaultProps: { disableFocusRipple: true },
        styleOverrides: {
          root: {
            padding: '8px 8px 8px 8px',
            backgroundColor: 'transparent',
            background: 'transparent',
            transition: 'none',
            '@media (max-width:540.95px)': {
              height: '40px',
              width: '40px',
              paddingTop: '8px',
              paddingRight: '8px',
              paddingBottom: '8px',
              paddingLeft: '8px',
            },
            color: '#323334',
            '&:hover': { backgroundColor: 'transparent' },
            '&.iconButtonContained': {
              backgroundColor: '#FFFFFF',
              color: '#323334',
              borderColor: '#323334',
              borderStyle: 'solid',
              borderWidth: '1px',
              padding: '8px 8px 8px 8px',
              '&:hover': {
                background: '#F2F2F2',
                borderWidth: '1px',
                borderColor: '#323334',
                color: '#323334',
              },
              '&:active': {
                backgroundColor: '#323334',
                borderColor: '#323334',
                color: '#FFFFFF',
              },
              '&.Mui-focusVisible': { boxShadow: 'none' },
            },
          },
          colorPrimary: {
            '&:hover': { backgroundColor: 'transparent' },
            '&.iconButtonContained': {
              backgroundColor: '#002677',
              borderColor: '#002677',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#FBF9F4',
              padding: '8px 8px 8px 8px',
              '&:hover': {
                backgroundColor: '#0c55B8',
                borderColor: '#0c55B8',
                borderWidth: '1px',
                color: '#FBF9F4',
              },
              '&:active': {
                backgroundColor: '#FBF9F4',
                borderColor: '#0c55B8',
                color: '#0c55B8',
                boxShadow: 'none',
              },
            },
            '&.iconButtonContainedHC': {
              backgroundColor: '#FBF9F4',
              borderColor: '#FBF9F4',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#002677',
              padding: '8px 8px 8px 8px',
              '&:hover': {
                backgroundColor: '#FBF9F4',
                borderWidth: '1px',
                borderColor: '#FBF9F4',
                color: '#0c55B8',
              },
              '&:active': {
                backgroundColor: 'transparent',
                borderColor: '#FBF9F4',
                boxShadow: 'none',
                color: '#FBF9F4',
              },
              '&.Mui-focusVisible:after': {
                outline: '2px solid #FFFFFF',
              },
            },
            '&.iconButtonGhostHC': {
              borderColor: 'transparent',
              borderStyle: 'solid',
              borderWith: '1px',
              backgroundColor: 'transparent',
              color: '#FBF9F4',
              padding: '8px 8px 8px 8px',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,1)',
                borderColor: '#FBF9F4',
                borderWith: '1px',
                color: '#FBF9F4',
              },
              '&:active': {
                backgroundColor: 'transparent',
                borderColor: '#FBF9F4',
                color: '#FBF9F4',
              },
              '&.Mui-focusVisible:after': {
                outline: '2px solid #FFFFFF',
              },
              '@media (max-width:540.95px)': {
                maxHeight: '40px',
                maxWidth: '40px',
                minWidth: '40px',
                paddingTop: '8px',
                paddingRight: '8px',
                paddingBottom: '8px',
                paddingLeft: '8px',
              },
            },
            '&.iconButtonGhost': {
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              borderStyle: 'solid',
              borderWidth: '1px',
              color: '#002677',
              padding: '8px 8px 8px 8px',
              '&:hover:not(:focus-within)': {
                backgroundColor: '#FBF9F4',
                borderColor: '#0c55B8',
                color: '#0c55B8',
              },
              '&:active:not(:focus-within)': {
                borderColor: '#002677',
                backgroundColor: '#FFFFFF',
                color: '#002677',
              },
              '&.Mui-disabled': {
                backgroundColor: '#F2F2F2',
                borderColor: '#F2F2F2',
              },
            },
          },
          colorSecondary: {
            '&:hover': { backgroundColor: 'transparent' },
            '&.iconButtonContained': {
              backgroundColor: '#FBF9F4',
              borderRadius: '50%',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: '#002677',
              color: '#002677',
              padding: '8px 8px 8px 8px',
              '&:hover': {
                backgroundColor: '#FBF9F4',
                borderWidth: '1px',
                borderColor: '#0c55B8',
                color: '#0c55B8',
              },
              '&:active': {
                backgroundColor: '#0c55B8',
                borderColor: '#0c55B8',
                boxShadow: 'none',
                color: '#FBF9F4',
              },
              '&.Mui-focusVisible': { boxShadow: 'none' },
              '&.Mui-disabled': {
                backgroundColor: '#F2F2F2',
                borderColor: '#F2F2F2',
              },
              '&.Mui-focusVisible:after': {
                outline: '2px solid #0c55B8',
              },
            },
            '&.iconButtonContainedHC': {
              backgroundColor: 'transparent',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: '#FBF9F4',
              color: '#FBF9F4',
              padding: '8px 8px 8px 8px',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.08)',
                borderWidth: '1px',
                borderColor: '#FBF9F4',
                color: '#FBF9F4',
              },
              '&:active': {
                backgroundColor: '#0c55B8',
                borderColor: '#FBF9F4',
                boxShadow: 'none',
                color: '#FBF9F4',
              },
              '&.Mui-focusVisible:after': {
                outline: '2px solid #FFFFFF',
              },
            },
            '&.iconButtonGhost': {
              color: '#323334',
              backgroundColor: 'transparent',
              borderRadius: '50%',
              borderColor: 'transparent',
              borderStyle: 'solid',
              borderWidth: '1px',
              padding: '8px 8px 8px 8px',
              '&:hover': {
                backgroundColor: 'rgba(25, 25, 26, 0.08)',
                borderWidth: '1px',
                borderColor: '#323334',
                color: '#323334',
              },
              '&:active': {
                borderColor: '#323334',
                backgroundColor: '#FFFFFF',
                color: '#323334',
              },
              '&.Mui-disabled': {
                backgroundColor: '#F2F2F2',
                borderColor: '#F2F2F2',
              },
            },
          },
          sizeSmall: {
            '@media (min-width:0px)': {
              height: '32px',
              width: '32px',
              paddingTop: '7px',
              paddingRight: '7px',
              paddingBottom: '7px',
              paddingLeft: '7px',
            },
          },
        },
      },
      MuiLink: {
        defaultProps: { variant: 'body2', underline: 'hover' },
        styleOverrides: {
          root: {
            '&.MuiTypography-h1': {
              color: '#0c55B8',
              '&:hover': { color: '#002677' },
              '&.Mui-focusVisible': {
                outline: '2px solid #002677',
              },
            },
            '&.MuiTypography-h2': {
              color: '#0c55B8',
              '&:hover': { color: '#002677' },
              '&.Mui-focusVisible': {
                outline: '2px solid #002677',
              },
            },
            '&.MuiTypography-h3': {
              color: '#0c55B8',
              '&:hover': { color: '#002677' },
              '&.Mui-focusVisible': {
                outline: '2px solid #002677',
              },
            },
            '&.MuiTypography-h4': {
              color: '#0c55B8',
              '&:hover': { color: '#002677' },
              '&.Mui-focusVisible': {
                outline: '2px solid #002677',
              },
            },
            '&.MuiTypography-h5': {
              color: '#0c55B8',
              '&:hover': { color: '#002677' },
              '&.Mui-focusVisible': {
                outline: '2px solid #002677',
              },
            },
            '&.MuiTypography-h6': {
              color: '#0c55B8',
              '&:hover': { color: '#002677' },
              '&.Mui-focusVisible': {
                outline: ' 2px solid #002677',
              },
            },
            '&.MuiTypography-body1, &.MuiTypography-body2': {
              color: '#0c55B8',
              '&:hover': { color: '#002677' },
              '&.Mui-focusVisible': {
                outline: '2px solid #0c55B8',
              },
            },
            '&.has-icon': {
              alignItems: 'center',
              display: 'inline-flex',
            },
            '& > .MuiSvgIcon-root': {
              fontSize: 'inherit',
              height: '1.5em',
              marginLeft: 2,
              width: '1.5em',
            },
            '&.is-bold': { fontWeight: '700' },
          },
          underlineAlways: {
            textDecoration: 'underline',
            '&:hover': { textDecoration: 'none' },
          },
          button: { fontWeight: 'bold' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            '&.clickableActionCard, &.selectableCard, &.expandableSelectableCard':
              {
                width: '100%',
                boxShadow: '0 1px 3px 0 #F2F2F2',
                '&:focus-within': {
                  outline: '2px solid #0c55B8',
                },
                '&.MuiPaper-elevation1': {
                  boxShadow: '0 1px 3px 0 #F2F2F2',
                },
                '&.MuiPaper-rounded': { borderRadius: 12 },
                '& .MuiCardActionArea-root.Mui-focusVisible .MuiCardActionArea-focusHighlight':
                  { opacity: 0 },
                '& .MuiTypography-h6.MuiTypography-gutterBottom':
                  {
                    marginBottom: 4,
                  },
              },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '&.clickableActionCard, &.selectableCard, &.expandableSelectableCard':
              {
                padding: '24px',
                '@media (max-width:540.95px)': {
                  padding: '16px',
                },
              },
          },
        },
      },
      MuiBreadcrumbs: {
        defaultProps: { color: 'textPrimary' },
        styleOverrides: {
          root: {
            '& .MuiButtonBase-root': {
              backgroundColor: 'transparent',
              marginLeft: 0,
              marginRight: 0,
              borderRadius: 0,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'transparent',
              color: '#0c55B8',
              height: '32px',
              width: '32px',
              paddingTop: '7px',
              paddingRight: '7px',
              paddingBottom: '7px',
              paddingLeft: '7px',
              '&:hover': {
                backgroundColor: '#FBF9F4',
                borderColor: '#0c55B8',
                borderWidth: '1px',
                borderTopRightRadius: '50%',
                borderBottomRightRadius: '50%',
                borderBottomLeftRadius: '50%',
                borderTopLeftRadius: '50%',
                color: '#0c55B8',
              },
              '&:focus, &.Mui-focusVisible': {
                backgroundColor: 'transparent',
                borderColor: '#0c55B8',
                borderWidth: '2px',
                borderStyle: 'solid',
              },
              '&.Mui-focusVisible .MuiTouchRipple-root': {
                display: 'none',
              },
              '&:active': {
                backgroundColor: '#FFFFFF',
                borderColor: '#002677',
                borderWidth: '1px',
                color: '#002677',
              },
            },
          },
          li: {
            '& a': {
              fontWeight: '700',
              '@media (max-width:540.95px)': {
                fontSize: '14.22px',
                fontWeight: '700',
                lineHeight: '18px',
              },
            },
            '& .currentPage': {
              color: '#4B4D4F',
              fontSize: '14.22px',
              fontWeight: '400',
              lineHeight: '18px',
              '@media (max-width:540.95px)': {
                fontSize: '14.22px',
                fontWeight: '400',
                lineHeight: '18px',
              },
            },
          },
          separator: {
            marginLeft: 0,
            marginRight: 0,
            color: '#4B4D4F',
            height: '24px',
            width: '24px',
          },
        },
      },
      MuiSwitch: {
        defaultProps: {
          color: 'primary',
          disableRipple: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: { padding: 0, height: 20, width: 40 },
          switchBase: {
            padding: 0,
            boxShadow: 'none',
            backgroundColor: '#FFFFFF',
            '&:hover': { backgroundColor: 'transparent' },
            '&:focus-within': { outline: 'none' },
            '&.Mui-focusVisible:after, &:focus-within:after': {
              content: 'none',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              borderColor: '#B1B2B4',
            },
            '&.Mui-disabled+.MuiSwitch-track': {
              backgroundColor: '#F2F2F2',
              borderColor: '#B1B2B4',
              opacity: 1,
            },
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#0c55B8',
              backgroundColor: 'transparent',
              '> .MuiSvgIcon-root': {
                height: '20px',
                width: '20px',
              },
              '&:hover': { color: '#002677' },
            },
            '&.Mui-checked:hover + .MuiSwitch-track': {
              borderColor: '#002677',
            },
            '&.Mui-checked+.MuiSwitch-track': {
              backgroundColor: '#FFFFFF',
              borderColor: '#0c55B8',
              opacity: 1,
            },
          },
          thumb: {
            boxShadow: 'none',
            backgroundColor: '#FFFFFF',
            borderColor: '#4B4D4F',
            borderStyle: 'solid',
            borderWidth: '2px',
            borderRadius: '50%',
            height: '20px',
            width: '20px',
          },
          track: {
            backgroundColor: '#F2F2F2',
            borderColor: '#4B4D4F',
            borderStyle: 'solid',
            borderWidth: '2px',
            width: '36px',
            height: '20px',
            borderRadius: '41px',
            opacity: 1,
          },
        },
      },
      MuiTab: {
        defaultProps: { focusRipple: false },
        styleOverrides: {
          root: {
            fontSize: '18px',
            fontWeight: '700',
            lineHeight: '24px',
            color: '#0c55B8',
            padding: '0 16px 16px 16px',
            borderBottom: '1px solid',
            borderBottomColor: '#B1B2B4',
            marginTop: 4,
            marginBottom: 4,
            textTransform: 'none',
            '&:hover': { color: '#002677' },
            '&.Mui-focusVisible:after': { content: 'none' },
            '&.Mui-focusVisible': {
              outlineWidth: '2px',
              outlineStyle: 'solid',
              outlineColor: '#0c55B8',
              outlineOffset: 2,
            },
            '&.Mui-selected': { color: '#002677' },
          },
        },
      },
      MuiTabs: {
        defaultProps: { centered: true, variant: 'fullWidth' },
        styleOverrides: {
          root: { marginBottom: '13px' },
          flexContainer: { marginRight: 4, marginLeft: 4 },
          indicator: {
            background: '#FF612B',
            height: '4px',
            borderRadius: '4px 4px 0 0',
            bottom: '4px',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          gutterBottom: { marginBottom: '24px' },
          paragraph: { marginBottom: '16px' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFF',
            border: '1px solid #4B4D4F',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            color: '#323334',
            fontSize: '14.22px',
            '& .MuiChip-icon': { height: '20px', width: '20px' },
            '& .MuiChip-deleteIcon': {
              height: '20px',
              width: '20px',
            },
            '& .MuiChip-iconSmall': {
              height: '15px',
              width: '15px',
            },
            '& .MuiChip-deleteIconSmall': {
              height: '15px',
              width: '15px',
            },
            '&.MuiChip-customAlert': {
              color: '#323334',
              fontWeight: '400',
              '& .MuiChip-icon': { color: '#323334' },
              '& .MuiChip-icon:hover': { color: '#323334' },
              backgroundColor: '#FCF0F0',
              borderColor: '#FCF0F0',
            },
            '&.MuiChip-customSuccess': {
              color: '#323334',
              fontWeight: '400',
              '& .MuiChip-icon': { color: '#323334' },
              '& .MuiChip-icon:hover': { color: '#323334' },
              backgroundColor: '#EFF6EF',
              borderColor: '#EFF6EF',
            },
            '&.MuiChip-customInfo': {
              color: '#323334',
              fontWeight: '400',
              '& .MuiChip-icon': { color: '#323334' },
              '& .MuiChip-icon:hover': { color: '#323334' },
              backgroundColor: '#E5F8FB',
              borderColor: '#E5F8FB',
            },
            '&.MuiChip-customWarning': {
              color: '#323334',
              fontWeight: '400',
              '& .MuiChip-icon': { color: '#323334' },
              '& .MuiChip-icon:hover': { color: '#323334' },
              backgroundColor: '#FEF9EA',
              borderColor: '#FEF9EA',
            },
            '&.MuiChip-customNeutral': {
              color: '#323334',
              fontWeight: '400',
              '& .MuiChip-icon': { color: '#323334' },
              '& .MuiChip-icon:hover': { color: '#323334' },
              backgroundColor: 'transparent',
              border: '1px solid #4B4D4F',
            },
            '&.MuiChip-customTransparent': {
              color: '#323334',
              fontWeight: '400',
              '& .MuiChip-icon': { color: '#323334' },
              '& .MuiChip-icon:hover': { color: '#323334' },
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              '.MuiChip-label:only-child': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiChip-icon, & .MuiChip-icon:hover': {
                color: '#FF612B',
              },
            },
          },
          sizeSmall: { fontSize: '12.64px' },
          clickable: {
            backgroundColor: '#002677',
            borderColor: '#002677',
            color: '#FFFFFF',
            fontWeight: '700',
            '& .MuiChip-icon': { color: '#FFFFFF' },
            '& .MuiChip-deleteIcon': {
              backgroundColor: 'transparent',
              color: '#FFFFFF',
              '&:hover': { color: '#FFFFFF' },
            },
            '&.MuiChip-deletable:focus': {
              backgroundColor: '#002677',
              borderColor: '#002677',
            },
            '&.Mui-focusVisible': { backgroundColor: '#002677' },
            '&.Mui-focusVisible:after': {
              borderRadius: '0',
              bottom: '-4px',
              content: '""',
              display: 'block',
              left: '-4px',
              outline: '2px solid #0c55B8',
              position: 'absolute',
              right: '-4px',
              top: '-4px',
            },
            '&:hover, &:active, &.MuiChip-deletable:hover, &.MuiChip-deletable:active':
              { backgroundColor: '#0c55B8' },
          },
          clickableColorPrimary: {
            backgroundColor: '#002677',
            borderColor: '#002677',
            color: '#FFFFFF',
            fontWeight: '700',
            '& .MuiChip-icon': { color: '#FFFFFF' },
            '& .MuiChip-deleteIcon': {
              backgroundColor: 'transparent',
              color: '#FFFFFF',
              '&:hover': { color: '#FFFFFF' },
            },
            '&.MuiChip-deletable:focus': {
              backgroundColor: '#002677',
              borderColor: '#002677',
            },
            '&.Mui-focusVisible': { backgroundColor: '#002677' },
            '&.Mui-focusVisible:after': {
              borderRadius: '0',
              bottom: '-4px',
              content: '""',
              display: 'block',
              left: '-4px',
              outline: '2px solid #0c55B8',
              position: 'absolute',
              right: '-4px',
              top: '-4px',
            },
            '&:hover, &:active, &.MuiChip-deletable:hover, &.MuiChip-deletable:active':
              { backgroundColor: '#0c55B8' },
          },
          clickableColorSecondary: {
            backgroundColor: '#002677',
            borderColor: '#002677',
            color: '#FFFFFF',
            fontWeight: '700',
            '& .MuiChip-icon': { color: '#FFFFFF' },
            '& .MuiChip-deleteIcon': {
              backgroundColor: 'transparent',
              color: '#FFFFFF',
              '&:hover': { color: '#FFFFFF' },
            },
            '&.MuiChip-deletable:focus': {
              backgroundColor: '#002677',
              borderColor: '#002677',
            },
            '&.Mui-focusVisible': { backgroundColor: '#002677' },
            '&.Mui-focusVisible:after': {
              borderRadius: '0',
              bottom: '-4px',
              content: '""',
              display: 'block',
              left: '-4px',
              outline: '2px solid #0c55B8',
              position: 'absolute',
              right: '-4px',
              top: '-4px',
            },
            '&:hover, &:active, &.MuiChip-deletable:hover, &.MuiChip-deletable:active':
              { backgroundColor: '#0c55B8' },
          },
          outlined: {
            backgroundColor: '#FFF',
            borderColor: '#0c55B8',
            color: '#0c55B8',
            '& .MuiChip-icon': { color: '#0c55B8' },
            '& .MuiChip-deleteIcon': { color: '#0c55B8' },
            '.MuiChip-clickable&:focus': {
              backgroundColor: '#FFF',
            },
            '.MuiChip-clickable&:hover': {
              backgroundColor: '#0c55B8',
              color: '#FFFFFF',
              '& .MuiChip-icon, & .MuiChip-deleteIcon': {
                color: '#FFF',
              },
            },
          },
          outlinedPrimary: {
            backgroundColor: '#FFF',
            borderColor: '#0c55B8',
            color: '#0c55B8',
            '& .MuiChip-icon': { color: '#0c55B8' },
            '& .MuiChip-deleteIcon': { color: '#0c55B8' },
            '.MuiChip-clickable&:focus': {
              backgroundColor: '#FFF',
            },
            '.MuiChip-clickable&:hover': {
              backgroundColor: '#0c55B8',
              color: '#FFFFFF',
              '& .MuiChip-icon, & .MuiChip-deleteIcon': {
                color: '#FFF',
              },
            },
          },
          outlinedSecondary: {
            backgroundColor: '#FFF',
            borderColor: '#0c55B8',
            color: '#0c55B8',
            '& .MuiChip-icon': { color: '#0c55B8' },
            '& .MuiChip-deleteIcon': { color: '#0c55B8' },
            '.MuiChip-clickable&:focus': {
              backgroundColor: '#FFF',
            },
            '.MuiChip-clickable&:hover': {
              backgroundColor: '#0c55B8',
              color: '#FFFFFF',
              '& .MuiChip-icon, & .MuiChip-deleteIcon': {
                color: '#FFF',
              },
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            '& .box': {
              minHeight: '60vh',
              background: '#FBF9F4',
              borderRadius: '64px',
              '@media (max-width:768.95px)': {
                borderRadius: '24px',
              },
            },
          },
        },
      },
      MuiAccordion: {
        defaultProps: { square: true },
        styleOverrides: {
          root: {
            border: '1px solid #4B4D4F',
            background: '#FFFFFF',
            borderLeft: 'none',
            borderRight: 'none',
            boxShadow: 'none',
            maxWidth: '948px',
            '&.Mui-expanded': {
              margin: '0px 0',
              borderTopColor: 'transparent',
            },
            '&:before': { backgroundColor: 'white' },
            '& .MuiButtonBase-root': {
              '&.Mui-focusVisible': {
                '&:after': { outline: 'none' },
              },
              '& .MuiAccordionSummary-content': { margin: 0 },
            },
            '& .MuiCollapse-hidden': {
              borderBottom: '1px solid #4B4D4F',
            },
            '& .MuiSvgIcon-root': {
              width: '36px',
              height: '36px',
              color: '#323334',
              background: 'transparent',
              '@media (max-width:768.95px)': {
                width: '24px',
                height: '24px',
              },
            },
          },
          rounded: {
            '&:first-child': {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
            '&:last-child': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        defaultProps: {
          disableRipple: true,
          focusRipple: false,
          disableTouchRipple: true,
        },
        styleOverrides: {
          root: {
            gap: 16,
            padding: '24px',
            '@media (max-width:768.95px)': { padding: '16px' },
            '&.Mui-expanded': { minHeight: 'unset' },
            '&:hover': {
              backgroundColor: 'rgba(25, 25, 26, 0.08)',
            },
            '&:focus': { outline: '2px solid #0C55B8' },
          },
          content: {
            width: '100%',
            wordBreak: 'break-word',
            '& .MuiTypography-root': {
              color: '#323334',
              lineHeight: '24px',
              fontSize: '20.25px',
              fontWeight: 700,
              '@media (max-width:768.95px)': {
                lineHeight: '24px',
                fontSize: '18px',
              },
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            '& .accordiondetailImage': {
              marginRight: '16px',
              width: '90px',
              height: '90px',
              float: 'left',
              '@media (max-width:768.95px)': {
                width: '40px',
                height: '40px',
              },
            },
            '& .accordiondetailImageDetail2': {
              clear: 'left',
              marginTop: 20,
            },
            padding: '16px 24px 24px 24px',
            '@media (max-width:768.95px)': {
              padding: '8px 16px 16px 16px',
            },
            '& .MuiTypography-root': {
              color: '#4B4D4F',
              lineHeight: '20px',
              fontSize: '16px',
              fontWeight: 400,
              '@media (max-width:768.95px)': {
                lineHeight: '20px',
                fontSize: '16px',
                fontWeight: 400,
              },
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiFormControl-marginNormal': {
              marginTop: 0,
              marginBottom: 8,
            },
            '& .ccg-MuiOutlinedInput-root': {
              '.ccg-MuiAutocomplete-hasPopupIcon.ccg-MuiAutocomplete-hasClearIcon&':
                { paddingRight: 0 },
            },
          },
          popupIndicatorOpen: { transform: 'none' },
          listbox: {
            '& .ccg-MuiAutocomplete-option': {
              '&.Mui-focused': {
                backgroundColor: '#F2F2F2',
                color: '#323334',
              },
              '&.Mui-focusVisible': {
                backgroundColor: '#FFFFFF',
                color: '#323334',
                outline: '2px solid #0c55B8',
                outlineOffset: '-2px',
              },
              '&[aria-selected="true"]': {
                backgroundColor: '#0c55B8',
                color: '#FFFFFF',
                '&.Mui-focused': {
                  backgroundColor: '#0c55B8',
                  color: '#FFFFFF',
                  '@media (hover: none)': {
                    backgroundColor: 'transparent',
                  },
                },
                '&.Mui-focusVisible': {
                  backgroundColor: '#FFFFFF',
                  color: '#323334',
                  outline: '2px solid #0c55B8',
                  outlineOffset: '-2px',
                },
              },
            },
          },
          inputRoot: {
            '&[class*="MuiOutlinedInput-root"]': {
              padding: 0,
              backgroundColor: '#FFFFFF',
              '& .MuiAutocomplete-input': {
                padding: '10px 12px 10px 12px',
              },
              '.ccg-MuiAutocomplete-hasPopupIcon &, .ccg-MuiAutocomplete-hasClearIcon &':
                { paddingRight: 0 },
              '.ccg-MuiAutocomplete-hasPopupIcon.ccg-MuiAutocomplete-hasClearIcon &':
                { paddingRight: 0 },
              '& .ccg-MuiAutocomplete-input': {
                padding: '10px 12px 10px 12px',
              },
              '& .ccg-MuiAutocomplete-input:first-child': {
                paddingLeft: '12px',
              },
              '& .ccg-MuiAutocomplete-endAdornment': {
                right: '12px',
              },
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            border: '1px solid',
            padding: '16px 12px 16px 16px',
            flexWrap: 'wrap',
            position: 'relative',
            width: '100%',
            '@media (min-width:541px)': {
              alignItems: 'flex-start',
              flexWrap: 'nowrap',
            },
          },
          standardSuccess: {
            backgroundColor: '#EFF6EF',
            border: '1px solid',
            borderColor: '#007000',
            '& .ccg-MuiAlert-icon': { color: '#007000' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#007000',
            },
          },
          standardInfo: {
            backgroundColor: '#E5F8FB',
            border: '1px solid',
            borderColor: '#002677',
            '& .ccg-MuiAlert-icon': { color: '#002677' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#002677',
            },
          },
          standardWarning: {
            backgroundColor: '#FEF9EA',
            border: '1px solid',
            borderColor: '#F5B700',
            '& .ccg-MuiAlert-icon': { color: '#C9730D' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#323334',
            },
          },
          standardError: {
            backgroundColor: '#FCF0F0',
            border: '1px solid',
            borderColor: '#C40000',
            '& .ccg-MuiAlert-icon': { color: '#C40000' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#C40000',
            },
          },
          outlinedSuccess: {
            backgroundColor: '#EFF6EF',
            border: '1px solid',
            borderColor: '#007000',
            '& .ccg-MuiAlert-icon': { color: '#007000' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#007000',
            },
          },
          outlinedInfo: {
            backgroundColor: '#E5F8FB',
            border: '1px solid',
            borderColor: '#002677',
            '& .ccg-MuiAlert-icon': { color: '#002677' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#002677',
            },
          },
          outlinedWarning: {
            backgroundColor: '#FEF9EA',
            border: '1px solid',
            borderColor: '#F5B700',
            '& .ccg-MuiAlert-icon': { color: '#C9730D' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#323334',
            },
          },
          outlinedError: {
            backgroundColor: '#FCF0F0',
            border: '1px solid',
            borderColor: '#C40000',
            '& .ccg-MuiAlert-icon': { color: '#C40000' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#C40000',
            },
          },
          filledSuccess: {
            backgroundColor: '#EFF6EF',
            border: '1px solid',
            borderColor: '#007000',
            '& .ccg-MuiAlert-icon': { color: '#007000' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#007000',
            },
          },
          filledInfo: {
            backgroundColor: '#E5F8FB',
            border: '1px solid',
            borderColor: '#002677',
            '& .ccg-MuiAlert-icon': { color: '#002677' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#002677',
            },
          },
          filledWarning: {
            backgroundColor: '#FEF9EA',
            border: '1px solid',
            borderColor: '#F5B700',
            '& .ccg-MuiAlert-icon': { color: '#C9730D' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#323334',
            },
          },
          filledError: {
            backgroundColor: '#FCF0F0',
            border: '1px solid',
            borderColor: '#C40000',
            '& .ccg-MuiAlert-icon': { color: '#C40000' },
            '& .ccg-MuiAlert-message > .MuiAlertTitle-root': {
              color: '#C40000',
            },
          },
          icon: {
            marginRight: 8,
            fontSize: 20,
            padding: 0,
            opacity: 1,
          },
          message: {
            padding: 0,
            flexGrow: 1,
            margin: 0,
            color: '#4B4D4F',
            fontSize: '16px',
            lineHeight: '20px',
            maxWidth: '100%',
            '@media (max-width:540.95px)': {
              maxWidth: 'calc(100% - 64px)',
              fontSize: '16px',
              lineHeight: '20px',
            },
            '& > .MuiAlertTitle-root': {
              marginBottom: 0,
              fontSize: '16px',
              fontWeight: '700',
              lineHeight: '26px',
              '@media (max-width:540.95px)': {
                fontSize: '16px',
                lineHeight: '20px',
              },
            },
          },
          action: {
            alignSelf: 'stretch',
            flexBasis: '100%',
            flexShrink: 0,
            flexWrap: 'wrap',
            margin: 0,
            padding: 0,
            '@media (min-width:541px)': { flexBasis: 'auto' },
            '& > .MuiAlert-customAction': {
              display: 'block',
              marginTop: 16,
              width: '100%',
              '@media (min-width:541px)': {
                alignSelf: 'center',
                margin: 0,
                marginLeft: 8,
                marginRight: 8,
                minWidth: 'auto',
                width: 'auto',
              },
            },
            '& > *:not(.MuiAlert-customAction), & > *:not(.MuiAlert-customAction):hover':
              {
                marginTop: '-4px',
                marginRight: '-4px',
                alignSelf: 'flex-start',
                '@media (max-width:540.95px)': {
                  position: 'absolute',
                  top: 12,
                  right: 12,
                },
              },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            color: '#0c55B8',
            fontSize: '14.22px',
            fontWeight: '700',
            lineHeight: '18px',
            '&:hover': {
              opacity: 1,
              color: '#002677',
              backgroundColor: '#F2F2F2',
            },
            '&.Mui-focusVisible': {
              opacity: 1,
              color: '#002677',
              backgroundColor: '#F2F2F2',
            },
            '&.Mui-selected': {
              backgroundColor: '#002677',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#0c55B8' },
              '&.Mui-focusVisible': {
                backgroundColor: '#0c55B8',
              },
            },
            '&.Mui-disabled': { color: '#B1B2B4' },
            '@media (max-width:768.95px)': {
              fontSize: '14.22px',
              fontWeight: '700',
              lineHeight: '18px',
            },
          },
          ellipsis: {
            color: '#323334',
            '&:hover': {
              color: '#323334',
              backgroundColor: 'transparent',
            },
          },
          previousNext: {
            color: '#0c55B8',
            '&:hover': {
              color: '#0c55B8',
              backgroundColor: '#F2F2F2',
            },
            '&:active': {
              backgroundColor: '#002677',
              color: '#FFFFFF',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            '&.Material-baseCard': {
              maxWidth: '100%',
              padding: '24px',
              '@media (max-width:540.95px)': { padding: '16px' },
              position: 'relative',
              overflow: 'visible',
              '&.backdrop': {
                padding: '16px 24px',
                borderTopRightRadius: 0,
                borderBottomLeftRadius: '12px',
                borderTopLeftRadius: 0,
                borderBottomRightRadius: '12px',
                '@media (max-width:540.95px)': {
                  padding: '16px',
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px',
                },
              },
              '&.hasGutterBottom': {
                marginBottom: '24px',
                '@media (max-width:540.95px)': {
                  marginBottom: '16px',
                },
                '&:last-child': { marginBottom: 0 },
              },
              '&.zeroPadding': { padding: 0 },
              '&.clickable': {
                '&:hover': { backgroundColor: '#F2F2F2' },
                '& .hasEndAdornment': {
                  color: '#4B4D4F',
                  height: '24px',
                  width: '24px',
                },
                '& .clickableCardTitle': { color: '#323334' },
              },
              '&.dashedBorder': {
                border: 0,
                backgroundImage:
                  "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23333' stroke-width='1' stroke-dasharray='10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")",
              },
              '&.selectable': {
                '& .MuiFormControlLabel-root:active, & .MuiFormControlLabel-root:focus, & .MuiFormControlLabel-root:focus-within':
                  { outline: 'none' },
                '& .MuiFormControlLabel-root': {
                  margin: 0,
                  marginBottom: 4,
                  padding: 0,
                  width: '100%',
                  '@media (max-width:540.95px)': {
                    alignItems: 'flex-start',
                  },
                },
                '& .MuiFormControlLabel-label': {
                  margin: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 'unset',
                },
                '&:hover': { backgroundColor: '#F2F2F2' },
              },
              '& .hasOverlay': {
                position: 'initial',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  borderRadius: '12px',
                },
                '&.Mui-focusVisible:before': {
                  outline: '2px solid #0c55B8',
                },
                '&.Mui-focusVisible:after, &:focus-within:after':
                  {
                    content: 'none',
                  },
                '&.MuiLink-root.Mui-focusVisible': {
                  outline: 0,
                  textDecoration: 'underline',
                },
                '& > .MuiTouchRipple-root': {
                  borderRadius: '12px',
                  '@media (max-width:540.95px)': {
                    borderRadius: '8px',
                  },
                },
                '&.MuiButtonBase-root': {
                  wordBreak: 'break-word',
                },
              },
              '& .isPreferredChip.MuiChip-root .MuiChip-icon': {
                marginLeft: 0,
              },
              '&.highlightCard': {
                backgroundColor: '#D9F6FA',
                '> .MuiTypography-root': { color: '#002677' },
              },
              '&.defaultCard': { backgroundColor: 'white' },
              '&.shadedCard': { backgroundColor: '#FAFAFA' },
              '&.shadedBackdropCard': {
                backgroundColor: 'rgba(25,25,26,0.04)',
              },
              '&.transparentCard': {
                backgroundColor: 'transparent',
              },
            },
          },
          rounded: {
            '&.Material-baseCard': {
              borderRadius: '12px',
              '@media (max-width:540.95px)': {
                borderRadius: '8px',
              },
              '&.xsmallBorderRadius': {
                borderRadius: '4px',
                padding: '8px',
                '@media (max-width:540.95px)': {
                  borderRadius: '4px',
                  padding: '8px',
                },
              },
              '&.smallBorderRadius': {
                borderRadius: '8px',
                padding: '16px',
                '@media (max-width:540.95px)': {
                  borderRadius: '4px',
                  padding: '8px',
                },
              },
              '&.mediumBorderRadius': {
                borderRadius: '12px',
                padding: '24px',
                '@media (max-width:540.95px)': {
                  borderRadius: '8px',
                  padding: '16px',
                },
              },
              '&.largeBorderRadius': {
                borderRadius: '24px',
                padding: '32px',
                '@media (max-width:540.95px)': {
                  borderRadius: '12px',
                  padding: '16px',
                },
              },
              '&.xlargeBorderRadius': {
                borderRadius: '64px',
                '@media (max-width:540.95px)': {
                  borderRadius: '24px',
                  padding: '16px',
                },
              },
            },
          },
          outlined: {
            borderColor: '#B1B2B4',
            borderStyle: 'solid',
            borderWidth: 1,
          },
          elevation2: {
            '&.Material-baseCard.clickable, &.Material-baseCard.selectable':
              {
                boxShadow:
                  '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
              },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            '&[data-popper-placement*="bottom"] .arrow': {
              top: 0,
              left: 'calc(50% - 8px)',
              marginTop: '-8px',
              boxShadow: '-1px -1px 0 #323334',
            },
            '&[data-popper-placement*="bottom-end"] .arrow': {
              top: 0,
              left: 'calc(90% - 8px)',
              marginTop: '-8px',
              boxShadow: '-1px -1px 0 #323334',
            },
            '&[data-popper-placement*="bottom-start"] .arrow': {
              top: 0,
              left: 'calc(10% - 8px)',
              marginTop: '-8px',
              boxShadow: '-1px -1px 0 #323334',
            },
            '&[data-popper-placement*="top"] .arrow': {
              top: 'calc(100% - 8px)',
              left: 'calc(50% - 8px)',
              '&:before': {
                marginLeft: '-3px',
                marginTop: '-3px',
              },
            },
            '&[data-popper-placement*="right"] .arrow': {
              left: 0,
              marginLeft: '-8px',
              '&:before': {
                marginLeft: '1px',
                marginTop: '-3px',
              },
            },
            '&[data-popper-placement*="left"] .arrow': {
              right: 0,
              marginRight: '-8px',
              '&:before': {
                marginLeft: '-3px',
                marginTop: '1px',
              },
            },
            '& .arrow': {
              position: 'absolute',
              width: '16px',
              height: '16px',
              boxSizing: 'border-box',
              top: 'calc(50% - 8px)',
              backgroundColor: '#323334',
              transform: 'rotate(45deg)',
              boxShadow:
                '0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12)',
              '&:before': {
                content: '""',
                display: 'block',
                backgroundColor: '#FFFFFF',
                height: '18px',
                width: '18px',
              },
            },
          },
          paper: {
            backgroundColor: '#FFFFFF',
            borderColor: '#323334',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderRadius: '12px',
            padding: 16,
            overflow: 'unset',
            boxShadow:
              '0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12)',
            '@media (min-width:541px)': { maxWidth: '272px' },
            '@media (min-width:769px)': { maxWidth: '408px' },
            '& .popoverTitle': {
              color: '#323334',
              fontSize: '16px',
              fontWeight: '700',
              lineHeight: '20px',
              marginBottom: 4,
              '@media (max-width:540.95px)': {
                fontSize: '16px',
                fontWeight: '700',
                lineHeight: '20px',
              },
            },
            '& .popoverBody': {
              color: '#323334',
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '20px',
              '@media (max-width:540.95px)': {
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '20px',
              },
            },
            '& .iconButton-popoverClose': { color: '#323334' },
          },
        },
      },
      MuiToggleButton: {
        defaultProps: {
          disableRipple: false,
          focusRipple: false,
          disableTouchRipple: false,
        },
        styleOverrides: {
          root: {
            padding: '10px 24px',
            color: '#0c55B8',
            fontSize: '16px',
            fontWeight: '700',
            borderColor: '#0c55B8',
            borderRadius: 4,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#0c55B8',
              borderColor: '#0c55B8',
              color: '#FFFFFF',
            },
            '&.Mui-selected': {
              backgroundColor: '#002677',
              color: '#FFFFFF',
              borderColor: '#002677',
              '&:hover': { backgroundColor: '#0c55B8' },
            },
            '@media (max-width:540.95px)': {
              fontSize: '16px',
              lineHeight: '20px',
              weight: '700',
            },
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            justifyContent: 'flex-start',
            marginBottom: 16,
            '@media (max-width:768.95px)': {
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            },
          },
        },
      },
      MuiStep: {
        styleOverrides: {
          root: {
            '.ccg-MuiStepButton-root:hover': {
              backgroundColor: 'rgba(25,25,26,0.08)',
              borderRadius: '4px',
            },
            padding: 0,
          },
          horizontal: {
            '.ccg-MuiStepLabel-root': {
              '@media (max-width:768.95px)': {
                alignItems: 'center',
                flexDirection: 'column',
              },
            },
          },
        },
      },
      MuiStepButton: {
        styleOverrides: {
          root: {
            margin: 0,
            padding: '4px',
            justifyContent: 'flex-start',
            width: 'auto',
            '@media (max-width:768.95px)': {
              padding: '0px 16px',
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: '#FFFFFF',
            height: '20px',
            width: '20px',
            '&.Mui-completed': { color: '#002677' },
            '&.Mui-active': {
              color: '#19191A',
              '.ccg-MuiStepIcon-text': { fill: '#FFFFFF' },
            },
          },
          text: {
            fill: '#4B4D4F',
            fontSize: '12.64px',
            fontWeight: '700',
            lineHeight: '16px',
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            color: '#4B4D4F',
            fontWeight: '400',
            margin: '0 0 0 4px',
            '&.Mui-active': {
              color: '#323334',
              fontSize: '14.22px',
              fontWeight: '700',
              lineHeight: '18px',
            },
            '&.ccg-MuiStepLabel-alternativeLabel': {
              margin: '4px 0 0 0',
            },
            '@media (max-width:768.95px)': {
              margin: '4px 0 0 0',
              textAlign: 'center',
            },
          },
          iconContainer: {
            paddingRight: 0,
            borderColor: '#4B4D4F',
            borderStyle: 'solid',
            borderWidth: '2px',
            borderRadius: '50%',
            '&.Mui-active': {
              backgroundColor: '#19191A',
              borderColor: '#19191A',
            },
            '&.Mui-completed': {
              borderColor: '#002677',
              background:
                'radial-gradient(circle, rgba(0,38,119,0) 60%, rgba(0,38,119,1) 65%)',
            },
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          root: {
            marginRight: 4,
            flexBasis: '40px',
            maxWidth: '40px',
            '@media (max-width:768.95px)': { display: 'none' },
          },
          line: {
            borderTopColor: '#CBCCCD',
            borderTopWidth: '2px',
            borderTopStyle: 'solid',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: 'rgba(25, 25, 26, 0.7)' },
          invisible: { backgroundColor: 'transparent' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            '@media (max-width:540.95px)': {
              '> .MuiDialog-container.MuiDialog-scrollPaper': {
                height: '90%',
                position: 'absolute',
                width: '100%',
                bottom: 0,
              },
            },
          },
          paper: {
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 4px 12px rgba(25,25,26,0.16)',
            '@media (max-width:540.95px)': {
              padding: '16px',
              borderRadius: 0,
            },
            '@media (min-width:541px)': {
              width: 'auto',
              height: 'auto',
            },
          },
          paperWidthSm: { maxWidth: '408px', padding: '24px' },
          paperWidthMd: { maxWidth: '624px', padding: '32px' },
          paperWidthLg: { maxWidth: '840px', padding: '40px' },
          paperWidthXl: { maxWidth: '1056px', padding: '48px' },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            '.ccg-MuiButtonBase-root:focus-within:after, .ccg-MuiLink-root:focus-within:after':
              {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: '-4px',
                bottom: '-4px',
                left: '-4px',
                right: '-4px',
                borderRadius: 0,
                outline: '2px solid #0c55B8',
              },
            '.ccg-MuiButtonBase-root.ccg-MuiCheckbox-root:focus-within:after, .ccg-MuiButtonBase-root.ccg-MuiRadio-root:focus-within:after':
              { content: 'none' },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { marginBottom: 16, padding: 0 },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            justifyContent: 'flex-start',
            marginTop: 16,
            padding: 0,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root: {
            '& .MuiDialogTitle-root': {
              padding: '40px',
              paddingBottom: '8px',
              '@media (max-width:768.95px)': {
                padding: '16px',
                paddingBottom: 0,
              },
              '&.hasHeadingShadow': {
                boxShadow: '0px 4px 12px rgba(25,25,26,0.16)',
                zIndex: 1,
              },
            },
            '& .MuiDialogContent-root': {
              margin: 0,
              padding: '40px',
              '@media (max-width:768.95px)': { padding: '16px' },
            },
            '& .MuiDialogTitle-root+.MuiDialogContent-root': {
              paddingTop: 16,
            },
            '& .MuiDialogActions-root': {
              paddingLeft: '40px',
              paddingRight: '40px',
              paddingTop: '24px',
              paddingBottom: '24px',
              justifyContent: 'center',
              '@media (max-width:768.95px)': {
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '16px',
                paddingBottom: '16px',
                '> .MuiButtonBase-root': { width: '100%' },
              },
              '&.hasFooterShadow': {
                boxShadow: '0px -4px 16px rgba(0,0,0,0.16)',
              },
            },
          },
          paper: {
            borderRadius: '12px 0px 0px 12px',
            backgroundColor: '#FFFFFF',
            '@media (max-width:768.95px)': {
              borderRadius: '12px 12px 0px 0px',
            },
          },
        },
      },
    },
    palette: {
      mode: 'light',
      primary: {
        main: '#002677',
        light: '#D9F6FA',
        dark: '#0c55B8',
        contrastText: '#fff',
      },
      secondary: {
        main: '#FBF9F4',
        light: '#FBF9F4',
        dark: '#D9F6FA',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: '#C40000',
        light: '#FCF0F0',
        dark: '#C40000',
        contrastText: '#fff',
      },
      warning: {
        main: '#F5B700',
        light: '#FEF9EA',
        dark: '#F5B700',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      info: {
        main: '#E5F8FB',
        light: '#FAFAFA',
        dark: '#F2F2F2',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      success: {
        main: '#007000',
        light: '#EFF6EF',
        dark: '#007000',
        contrastText: '#fff',
      },
      grey: {
        '50': '#FAFAFA',
        '100': '#F2F2F2',
        '200': '#E5E5E6',
        '300': '#CBCCCD',
        '400': '#B1B2B4',
        '500': '#929496',
        '600': '#6E7072',
        '700': '#4B4D4F',
        '800': '#323334',
        '900': '#19191A',
        A100: '#f5f5f5',
        A200: '#eeeeee',
        A400: '#bdbdbd',
        A700: '#616161',
      },
      text: {
        primary: '#4B4D4F',
        secondary: '#323334',
        disabled: '#B1B2B4',
      },
      background: {
        paper: '#FFFFFF',
        default: '#FFFFFF',
      },
      common: { black: '#000', white: '#fff' },
      contrastThreshold: 3,
      tonalOffset: 0.2,
      divider: 'rgba(0, 0, 0, 0.12)',
      action: {
        active: 'rgba(0, 0, 0, 0.54)',
        hover: 'rgba(0, 0, 0, 0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(0, 0, 0, 0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        disabledOpacity: 0.38,
        focus: 'rgba(0, 0, 0, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
      },
    },
    shape: {
      borderRadius: 4,
    },
    typography: {
      fontWeightLight: '400',
      fontWeightRegular: '400',
      fontWeightMedium: '700',
      fontWeightBold: '900',
      fontFamily: '"Optum Sans", Arial, sans-serif',
      h1: {
        color: '#002677',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '36.48px',
        fontWeight: '700',
        lineHeight: '48px',
        '@media (max-width:540.95px)': {
          fontWeight: '700',
          fontSize: '32.43px',
          lineHeight: '40px',
        },
      },
      h2: {
        color: '#002677',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '32.43px',
        fontWeight: '700',
        lineHeight: '40px',
        '@media (max-width:540.95px)': {
          fontWeight: '700',
          fontSize: '28.83px',
          lineHeight: '36px',
        },
      },
      h3: {
        color: '#002677',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '28.83px',
        fontWeight: '700',
        lineHeight: '36px',
        '@media (max-width:540.95px)': {
          fontWeight: '700',
          fontSize: '25.63px',
          lineHeight: '32px',
        },
      },
      h4: {
        color: '#4B4D4F',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '25.63px',
        fontWeight: '700',
        lineHeight: '32px',
        '@media (max-width:540.95px)': {
          fontWeight: '700',
          fontSize: '22.78px',
          lineHeight: '28px',
        },
      },
      h5: {
        color: '#4B4D4F',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '22.78px',
        fontWeight: '700',
        lineHeight: '28px',
        '@media (max-width:540.95px)': {
          fontWeight: '700',
          fontSize: '20.25px',
          lineHeight: '24px',
        },
      },
      h6: {
        color: '#4B4D4F',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '20.25px',
        fontWeight: '700',
        lineHeight: '24px',
        '@media (max-width:540.95px)': {
          fontWeight: '700',
          fontSize: '18px',
          lineHeight: '24px',
        },
      },
      subtitle1: {
        color: '#4B4D4F',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '18px',
        fontWeight: '400',
        lineHeight: '24px',
        '& strong': { fontWeight: '700' },
        '@media (max-width:540.95px)': {
          fontSize: '18px',
          fontWeight: '400',
          lineHeight: '24px',
        },
      },
      body1: {
        color: '#4B4D4F',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '20px',
        '& strong': { fontWeight: '700' },
        '@media (max-width:540.95px)': {
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '20px',
        },
      },
      body2: {
        color: '#4B4D4F',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '14.22px',
        fontWeight: '400',
        lineHeight: '18px',
        '& strong': { fontWeight: '700' },
        '@media (max-width:540.95px)': {
          fontSize: '14.22px',
          fontWeight: '400',
          lineHeight: '18px',
        },
      },
      subtitle2: {
        color: '#4B4D4F',
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontSize: '12.64px',
        fontWeight: '400',
        lineHeight: '16px',
        '& strong': { fontWeight: '700' },
        '@media (max-width:540.95px)': {
          fontSize: '12.64px',
          fontWeight: '400',
          lineHeight: '16px',
        },
      },
      htmlFontSize: 16,
      fontSize: 14,
      button: {
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontWeight: '700',
        fontSize: '0.875rem',
        lineHeight: 1.75,
        textTransform: 'uppercase',
      },
      caption: {
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontWeight: '400',
        fontSize: '0.75rem',
        lineHeight: 1.66,
      },
      overline: {
        fontFamily: '"Optum Sans", Arial, sans-serif',
        fontWeight: '400',
        fontSize: '0.75rem',
        lineHeight: 2.66,
        textTransform: 'uppercase',
      },
    },
    unstable_sxConfig: {
      border: { themeKey: 'borders' },
      borderTop: { themeKey: 'borders' },
      borderRight: { themeKey: 'borders' },
      borderBottom: { themeKey: 'borders' },
      borderLeft: { themeKey: 'borders' },
      borderColor: { themeKey: 'palette' },
      borderTopColor: { themeKey: 'palette' },
      borderRightColor: { themeKey: 'palette' },
      borderBottomColor: { themeKey: 'palette' },
      borderLeftColor: { themeKey: 'palette' },
      outline: { themeKey: 'borders' },
      outlineColor: { themeKey: 'palette' },
      borderRadius: { themeKey: 'shape.borderRadius' },
      color: { themeKey: 'palette' },
      bgcolor: {
        themeKey: 'palette',
        cssProperty: 'backgroundColor',
      },
      backgroundColor: { themeKey: 'palette' },
      zIndex: { themeKey: 'zIndex' },
      boxShadow: { themeKey: 'shadows' },
      fontFamily: { themeKey: 'typography' },
      fontSize: { themeKey: 'typography' },
      fontStyle: { themeKey: 'typography' },
      fontWeight: { themeKey: 'typography' },
      typography: { cssProperty: false, themeKey: 'typography' },
    },
    mixins: {
      toolbar: {
        minHeight: 56,
        '@media (min-width:0px)': {
          '@media (orientation: landscape)': { minHeight: 48 },
        },
        '@media (min-width:541px)': { minHeight: 64 },
      },
    },
    shadows: [
      'none',
      '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
      '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
      '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
      '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
      '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
      '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
      '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
      '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
      '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
      '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
      '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
      '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
      '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
      '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
      '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
      '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
      '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
      '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
    ],
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
    zIndex: {
      mobileStepper: 1000,
      fab: 1050,
      speedDial: 1050,
      appBar: 1100,
      drawer: 1200,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
    },
  },
  customThemeOptions,
);
